// =============================================================================
// Données du compteur non reçues, avant la première étude (DEV-2311, reco (p)
// ajustée par le juge du 20/10/2026, lot L4 ; maquette (p) v1 jugée).
//
// Ce que le client doit vivre : avant sa première étude, sur un compteur dont
// le moteur refuserait l'étude faute des informations du contrat, il lit tout
// de suite pourquoi et ce qu'il peut faire, au lieu de lancer une étude qui
// reviendrait refusée. Il garde la main : « Modifier les hypothèses » reste, et
// une formule saisie (ou un segment saisi, hors numéro inconnu) rend le
// lancement possible (R-T20, US18 CA1).
//
// Le critère est STRICTEMENT celui du moteur, `raise_si_collecte_empeche_la_fta`
// (courtage origin/main 72a0721, `modules/mixins/validate_etat_source.py`),
// recopié ici ligne à ligne, et vérifié contre lui par une table de cas
// partagée (`optim-turpe-v2.collecte.cas.json`) :
// - une formule SAISIE ou SERVIE par la Source : rien ;
// - `informations.pdl.data_retrieval.status` absent ou illisible, cause
//   absente, vide ou `a_jour` : rien (on n'affirme pas un défaut qu'on ne sait
//   pas) ;
// - `pdl_hors_service` : `compteur-resilie` ;
// - `identifiant_inconnu` : `compteur-identifiant-inconnu`, quel que soit le
//   segment ;
// - toute autre cause, y compris inédite : `compteur-donnees-non-recues`,
//   SEULEMENT sans segment connu (ni servi, ni saisi).
// Une collecte arrêtée sur un compteur dont la formule est connue ne bloque
// donc rien (Q23) : elle a son historique et donne un résultat chiffré.
//
// Les textes sont ceux que le moteur sert, mot pour mot (`mixins/runerrors.json`
// et `texte_donnees_non_recues`) : aucun texte nouveau à l'écran client.
// Le refus nommé du moteur reste servi pour l'API Open et la relance (Q28).
// =============================================================================
import type { SaisiesDuLancement } from "./optim-turpe-v2.echec"

/** Les refus que la garde du moteur lève, par leur code servi. */
export type RefusDeLaCollecte =
    | "compteur-resilie"
    | "compteur-identifiant-inconnu"
    | "compteur-donnees-non-recues"

/** Ce que la carte d'attente dit : le message et le remède servis. */
export type AttenteDesDonnees = {
    message: string
    remede: string
}

/** courtage `CAUSES_RECUPERATION_EN_COURS`. */
const CAUSES_RECUPERATION_EN_COURS = ["onboarding", "mise_a_jour_demandee"]

/** courtage `RELAIS_SI_CELA_TARDE`. */
const RELAIS_SI_CELA_TARDE =
    "si elles tardent, signalez ce compteur à votre interlocuteur habituel."

/** courtage `mixins/runerrors.json`, refus `compteur-identifiant-inconnu`. */
export const TEXTE_IDENTIFIANT_INCONNU: AttenteDesDonnees = {
    message: "Numéro de compteur inconnu du gestionnaire de réseau",
    remede: "Le gestionnaire de réseau ne connaît pas ce numéro de compteur : les informations de son contrat, dont la formule tarifaire d'acheminement, ne peuvent pas être lues, et l'étude en a besoin. Vérifiez le numéro du compteur, 14 chiffres, sur une facture d'électricité du site. S'il est faux, ajoutez le compteur avec le bon numéro, puis lancez l'étude depuis ce compteur.",
}

/** courtage `MESSAGE_RECUPERATION_EN_COURS` / `DESCRIPTION_RECUPERATION_EN_COURS`. */
export const TEXTE_RECUPERATION_EN_COURS: AttenteDesDonnees = {
    message: "Données du compteur en cours de récupération",
    remede:
        "La récupération des données de ce compteur est en cours : les informations de son contrat, dont la formule tarifaire d'acheminement, ne sont pas encore arrivées, et l'étude en a besoin. Lancez l'étude quand elles seront arrivées ; " +
        RELAIS_SI_CELA_TARDE,
}

/** courtage `MESSAGE_CONSENTEMENT_ABSENT` / `DESCRIPTION_CONSENTEMENT_ABSENT`. */
export const TEXTE_CONSENTEMENT_ABSENT: AttenteDesDonnees = {
    message: "Aucune autorisation valide pour lire les données de ce compteur",
    remede:
        "Sans autorisation valide, les informations du contrat de ce compteur, dont la formule tarifaire d'acheminement, ne peuvent pas être lues, et l'étude en a besoin. Le titulaire du compteur doit signer une autorisation d'accès à ses données ; lancez ensuite l'étude quand elles seront arrivées ; " +
        RELAIS_SI_CELA_TARDE,
}

/** courtage `MESSAGE_INFORMATIONS_NON_RECUES` (texte générique du catalogue). */
export const TEXTE_INFORMATIONS_NON_RECUES: AttenteDesDonnees = {
    message: "Informations du contrat de ce compteur non reçues",
    remede: "Les informations du contrat de ce compteur, dont la formule tarifaire d'acheminement et le segment, n'ont pas été reçues, et l'étude en a besoin ; sans segment connu, la formule ne peut pas être choisie dans les hypothèses de l'étude. Signalez ce compteur à votre interlocuteur habituel.",
}

/** Ce que la garde lit de la Source (dapi `GET /source/informations`). */
type SourceLue = {
    informations?: {
        pdl?: {
            compteur?: { segment?: unknown } | null
            structure_tarifaire?: {
                formule_tarifaire_acheminement?: { fta?: unknown } | null
            } | null
            data_retrieval?: { status?: unknown } | null
        } | null
    } | null
}

/** courtage `_segment_connu` : un segment saisi, ou servi en chaîne non vide. */
function segmentConnu(
    source: SourceLue | null | undefined,
    segmentSaisi: string | null | undefined
): boolean {
    if (segmentSaisi) return true
    const segment = source?.informations?.pdl?.compteur?.segment
    return typeof segment === "string" && segment !== ""
}

/** La cause servie par la collecte, ou `null` sans fait établi. */
function causeServie(source: SourceLue | null | undefined): string | null {
    const status = source?.informations?.pdl?.data_retrieval?.status
    if (!status || typeof status !== "object" || Array.isArray(status)) {
        return null
    }
    const cause = (status as { cause?: unknown }).cause
    if (typeof cause !== "string" || !cause || cause === "a_jour") return null
    return cause
}

/**
 * Le refus que le moteur lèverait au lancement faute de formule, ou `null`
 * (`raise_si_collecte_empeche_la_fta`, mêmes entrées, même ordre).
 */
export function refusDeLaCollecte(
    source: SourceLue | null | undefined,
    saisies: SaisiesDuLancement = {}
): RefusDeLaCollecte | null {
    // courtage : `fta_saisie is not None or <fta servie> is not None`.
    const ftaServie =
        source?.informations?.pdl?.structure_tarifaire
            ?.formule_tarifaire_acheminement?.fta
    if (saisies.fta != null || ftaServie != null) return null
    const cause = causeServie(source)
    if (cause === null) return null
    if (cause === "pdl_hors_service") return "compteur-resilie"
    if (cause === "identifiant_inconnu") return "compteur-identifiant-inconnu"
    if (segmentConnu(source, saisies.segment)) return null
    return "compteur-donnees-non-recues"
}

/**
 * La carte d'attente du mode `entree` : le message et le remède que le moteur
 * servirait, ou `null`. Le point hors service n'en a pas : il garde son refus
 * « compteur résilié » existant (maquette (p), hors carte).
 */
export function attenteDesDonnees(
    source: SourceLue | null | undefined,
    saisies: SaisiesDuLancement = {}
): AttenteDesDonnees | null {
    const refus = refusDeLaCollecte(source, saisies)
    if (refus === "compteur-identifiant-inconnu") {
        return TEXTE_IDENTIFIANT_INCONNU
    }
    if (refus !== "compteur-donnees-non-recues") return null
    // courtage `texte_donnees_non_recues`.
    const cause = causeServie(source)
    if (cause !== null && CAUSES_RECUPERATION_EN_COURS.includes(cause)) {
        return TEXTE_RECUPERATION_EN_COURS
    }
    if (cause === "consentement_absent") return TEXTE_CONSENTEMENT_ABSENT
    return TEXTE_INFORMATIONS_NON_RECUES
}
